import { ActionIcon, Avatar, Badge, Button, Container, Dialog, Divider, Grid, Group, LoadingOverlay, Menu, Modal, Paper, Popover, Text, Textarea, Timeline, Title, Tooltip, UnstyledButton } from '@mantine/core';
import { useClipboard, useFocusTrap } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import moment from 'moment';
import React from 'react';
import { FaAngleDoubleDown, FaAngleDoubleUp, FaAngleDown, FaAngleUp, FaBars, FaBookmark, FaRegArrowAltCircleRight, FaFile, FaMinus, FaPaperPlane, FaPlus, FaRegArrowAltCircleUp, FaRegBookmark, FaSearchLocation, FaTrash, FaCopy, FaBullseye, FaCheck, FaSquare } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useApp } from '../../contexts/app.context';
import { useAuth } from '../../contexts/auth.context';
import { DELETE_FILE, GET_FILE_TYPES, SAVE_FILE, UPLOAD } from '../../services/files';
import { FOLLOW_THEME, GET_THEME_COMMENTS, GET_THEME, INTERACT_THEME, COMMENT_THEME } from '../../services/themes';
import { spentTime } from '../../utility/util';
import InputField, { InputFieldTypes } from '../input_field';
import SimpleHeader from '../simple_header';
import { BadgeOrigin } from '../theme_paper';
import ThemeInsights from './insights';

export default function Details({ id }){
    const [data, setData] = React.useState<any>(null);
    const [formData, setFormData] = React.useState<any>(null);
    const [loadingDetails, setLoadingDetails] = React.useState(false);
    const [deletingFile, setDeletingFile] = React.useState(null);
    const [loadingDeleteFile, setLoadingDeleteFile] = React.useState(false);
    const [loadingComment, setLoadingComment] = React.useState(false);
    const [showInsights, setShowInsights] = React.useState(null);
    const [loadingUploadFile, setLoadingUploadFile] = React.useState(false);
    const [loadingSaveFile, setLoadingSaveFile] = React.useState(false);
    const [followTemp, setFollowTemp] = React.useState<any>(undefined);
    const [tags, setTags] = React.useState<string[]>([]);
    const [favorableTemp, setFavorableTemp] = React.useState<any>(undefined);
    const [priorityTemp, setPriorityTemp] = React.useState<any>(undefined);
    const [addFile, setAddFile] = React.useState(null);
    const [newComment, setNewComment] = React.useState({
        destin: 'public',
        comment: ""
    });
    const [fileTypes, setFileTypes] = React.useState([]);
    const [comments, setComments] = React.useState([]);
    const [showList, setShowList] = React.useState(null);
    
    const otherFileFocus = useFocusTrap(true);

    const clipboard = useClipboard({ timeout: 500 });
    
    const { role, userData } = useAuth();
    const { openTheme } = useApp();

    const loadDetails = () => {
        setLoadingDetails(true)
        
        GET_THEME(id)
        .then((theme) => {
            setLoadingDetails(false)
            setData(theme)
        })
        .catch(err => {
            setLoadingDetails(false)
            showNotification({color: 'red', message: err.message})
        })
    }

    const loadFileTypes = () => {
        GET_FILE_TYPES()
        .then((fts) => {
            setFileTypes(fts)
        })
        .catch(err => {
            showNotification({color: 'red', message: err.message})
        })
    }

    const loadComments = () => {
        GET_THEME_COMMENTS(id)
        .then((c) => {
            setComments(c)
        })
        .catch(err => {
            showNotification({color: 'red', message: err.message})
        })
    }
    
    const confirmDelete = () => {
        setLoadingDeleteFile(true)
        DELETE_FILE(deletingFile)
        .then(() => {
            loadDetails();
            setDeletingFile(null);
            setLoadingDeleteFile(false)
        })
        .catch(err => {
            setLoadingDeleteFile(false)
            showNotification({color: 'red', message: err.message})
        })
    }

    const follow = () => {
        const isF = isFollowing();
        setFollowTemp(!isF);
        
        FOLLOW_THEME(id, !isF)
        .then(res => {})
        .catch(err => {
            setFollowTemp(isF);
            showNotification({ title: "Ops", message: err.message })
        })
    }

    const uploadFile = (files: any[]) => {
        setLoadingUploadFile(true)
        const formData = new FormData();
        
        files.forEach(file => { formData.append(`files`, file) });
        UPLOAD(formData)
        .then(({value}) => {
            setLoadingUploadFile(false)
            setAddFile(af => ({...af, file: value}));
        }).catch(err => {
            setLoadingUploadFile(false)
            showNotification({message: err.message, color: 'red'})
            
        })
    }
    
    const saveFile = () => {
        setLoadingSaveFile(true)
        
        SAVE_FILE({
            ...addFile,
            theme_id: data.id
        })
        .then(() => {
            setLoadingSaveFile(false);
            setAddFile(null);
            loadDetails();
            loadFileTypes();
        }).catch(err => {
            setLoadingSaveFile(false)
            showNotification({message: err.message, color: 'red'})
            
        })
    }
    
    const saveComment = () => {
        if(!newComment.destin || !newComment.comment || newComment.comment.length <= 3){
            showNotification({message: "Insira seu comentário", color: 'red'})
        }else{
            setLoadingComment(true)
            
            COMMENT_THEME(id, newComment)
            .then(() => {
                setLoadingComment(false);
                loadComments();
                setNewComment({
                    destin: 'public',
                    comment: ""
                })
            }).catch(err => {
                setLoadingComment(false)
                showNotification({message: err.message, color: 'red'})
                
            })
        }
    }

    const interact = ({favorable, priority, tags} : {favorable?: string, priority?: string, tags?: string[]}) => {
        if(tags) setTags(tags);
        const isF = getFavorable();
        const isP = getPriority();

        let params : any = {};
        if(favorable !== undefined){
            params.favorable = favorable;
            setFavorableTemp(favorable);
        }
        if(tags){ params.tags = tags; }
        if(priority !== undefined){
            params.priority = priority;
            setPriorityTemp(priority);
        }
        
        INTERACT_THEME(data?.id, params)
        .then(res => {})
        .catch(err => {
            setFavorableTemp(isF);
            setPriorityTemp(isP);
            showNotification({ title: "Ops", message: err.message })
        })
    }
    
    const isFollowing = () => followTemp !== undefined ? followTemp : !!data?.follow;
    const getFavorable = () => favorableTemp !== undefined ? favorableTemp : data?.favorable;
    const getPriority = () => priorityTemp !== undefined ? priorityTemp : data?.priority;

    React.useEffect(() => {
        loadDetails();
        loadComments();
        loadFileTypes();

        setFollowTemp(undefined);
        setFavorableTemp(undefined);
        setPriorityTemp(undefined);

        setShowInsights(null);
    }, [id, role])
    React.useEffect(() => { setTags(data?.tags ?? []) }, [data])
    React.useEffect(() => { setFormData({...data}) }, [data])
    // React.useEffect(() => {
    //     if(addFile?.file_type_id === "other" && otherFileTypeRef.current?.focus){
    //         setTimeout(() => {
    //             otherFileTypeRef.current.focus();
    //         }, 300)
    //     }
    // }, [addFile?.file_type_id])

    return <div style={{padding: '20px 0'}}>
        <LoadingOverlay visible={loadingDetails} />

        <Group>    
            <div style={{flex: 1}}>
                {data?.origin && <BadgeOrigin origin={data?.origin} />}
            </div>

            <Group>
                <Group style={{flex: 1}}>
                    {tags.map(tag => <Badge>{tag}</Badge>)}
                </Group>

                <Menu closeOnItemClick={false}>
                    <Menu.Target><ActionIcon><FaBullseye /></ActionIcon></Menu.Target>
                    <Menu.Dropdown>
                        {(userData?.company?.tags ?? []).map(tag => (
                            <Menu.Item icon={tags.includes(tag) ? <FaCheck /> : <FaSquare color="#EFEFEF" />} onClick={() => { interact({
                                tags: tags.includes(tag)
                                    ? tags.filter(t => t !== tag)
                                    : [...tags,tag]
                            }) }}>{tag}</Menu.Item>
                        ))}
                    </Menu.Dropdown>
                </Menu>
            </Group>
            <ActionIcon onClick={() => window.open(data?.originUrl, "_blank")}>
                <FaRegArrowAltCircleRight color="gray" />
            </ActionIcon>
        </Group>
        <SimpleHeader title={data?.title} right={<Group>
            {role && <Menu>
                <Menu.Target>
                    <Button
                            variant={getPriority() ? "outline" : "subtle"}
                            color={{
                                'very-high': 'green',
                                'high': 'teal',
                                'medium': 'gray',
                                'low': 'yellow',
                                'very-low': 'red',
                            }[getPriority()] || null}
                            leftIcon={<FaBars />}
                        >{{
                            'very-high': "Prioridade - Muito alta",
                            'high': "Prioridade - Alta",
                            'medium': "Prioridade - Média",
                            'low': "Prioridade - Baixa",
                            'very-low': "Prioridade - Muito baixa",
                        }[getPriority()] || "Prioridade"}</Button>
                </Menu.Target>
                <Menu.Dropdown>
                    {[
                        {key: 'very-high', color: 'green', title: "Muito alta", icon: <FaAngleDoubleUp />},
                        {key: 'high', color: 'teal', title: "Alta", icon: <FaAngleUp />},
                        {key: 'medium', color: 'gray', title: "Média", icon: <FaMinus />},
                        {key: 'low', color: 'yellow', title: "Baixa", icon: <FaAngleDown />},
                        {key: 'very-low', color: 'red', title: "Muito baixa", icon: <FaAngleDoubleDown />},
                    ].map(item => (
                        <Button
                            onClick={() => interact({
                                priority: getPriority() === item.key
                                ? null : item.key})}
                            variant={getPriority() === item.key ? "filled" : "subtle"}
                            color={item.color}
                            leftIcon={item.icon}
                            styles={{
                                inner: {justifyContent: 'flex-start', textAlign: 'left', fontSize: 12},
                                icon: {padding: 0},
                                root: {marginBottom: 5}
                            }}
                            fullWidth
                        >{item.title}</Button>
                    ))}
                </Menu.Dropdown>
            </Menu>}
            {role && <Menu >
                <Menu.Target>
                    <Button
                            variant={getFavorable() ? "outline" : "subtle"}
                            color={['unfavorable', 'unfavorable-res'].includes(getFavorable())
                                ? 'red'
                                : ['favorable', 'favorable-res'].includes(getFavorable())
                                    ? 'green'
                                    : getFavorable() === 'neutral'
                                        ? 'gray'
                                        : null}
                            leftIcon={<FaBars />}
                        >{{
                            "favorable": "Favorável",
                            "favorable-res": "Favorável - com ressalvas",
                            "neutral": "Neutro",
                            "unfavorable": "Contra",
                            "unfavorable-res": "Contra - com ressalvas",
                        }[getFavorable()] || 'Posicionamento'}</Button>
                </Menu.Target>
                <Menu.Dropdown>
                    {[
                        {key: 'favorable', color: 'green', title: "Favorável", icon: <FaAngleDoubleUp />},
                        {key: 'favorable-res', color: 'green', title: "Favorável - com ressalvas", icon: <FaAngleUp />},
                        {key: 'neutral', color: 'gray', title: "Neutro", icon: <FaMinus />},
                        {key: 'unfavorable-res', color: 'red', title: "Contra - com ressalvas", icon: <FaAngleDown />},
                        {key: 'unfavorable', color: 'red', title: "Contra", icon: <FaAngleDoubleDown />},
                    ].map(item => (
                        <Button
                            onClick={() => interact({
                                favorable: getFavorable() === item.key
                                ? null : item.key})}
                            variant={getFavorable() === item.key ? "filled" : "subtle"}
                            color={item.color}
                            leftIcon={item.icon}
                            styles={{
                                inner: {justifyContent: 'flex-start', textAlign: 'left', fontSize: 12},
                                icon: {padding: 0},
                                root: {marginBottom: 5}
                            }}
                            fullWidth
                        >{item.title}</Button>
                    ))}
                </Menu.Dropdown>
            </Menu>}
            {role && <Button
                variant={isFollowing() ? "filled" : "subtle"}
                onClick={() => follow()}
                leftIcon={!isFollowing() ? <FaRegBookmark /> : <FaBookmark/>}
            >{!isFollowing() ? 'Acompanhar' : 'Deixar de Acompanhar'}</Button>}
            
            </Group>
        } />
        
        <Group style={{overflowX: 'auto', whiteSpace: 'nowrap'}}>
            {(data?.others || []).slice(0, 10).map((rel) => <>
                <Text color="blue" component={Link} to="#" onClick={() => openTheme(rel)}>{{
                    "camara": "CÂMARA DOS DEPUTADOS",
                    "senado": "SENADO FEDERAL",
                }[rel.origin] || "OUTROS"} - {rel.title}</Text>
            </>)}
        </Group>
        <Group style={{overflowX: 'auto', whiteSpace: 'nowrap'}}>
            {(data?.relations || []).length > 0 && <Text>Relacionadas: </Text>}
            {(data?.relations || []).slice(0, 10).map((rel) => <>
                <Text ml="xs" color="blue" component={Link} to="#" onClick={() => openTheme(rel)}>{rel.title}</Text>
                <Text ml="xs" color="blue">|</Text>
            </>)}
            {(data?.relations || []).length > 10 && <Text ml="xs" color="blue" style={{cursor: 'pointer'}} onClick={() => setShowList(data?.relations)}>Ver todas...</Text>}
        </Group>
        <Group style={{overflowX: 'auto', whiteSpace: 'nowrap'}}>
            {(data?.principals || []).length === 1 && <Text>Pauta principal: </Text>}
            {(data?.principals || []).length > 1 && <Text>Pautas principais: </Text>}
            {(data?.principals || []).slice(0, 10).map((rel) => <>
                <Text ml="xs" color="blue" component={Link} to="#" onClick={() => openTheme(rel)}>{rel.title}</Text>
                <Text ml="xs" color="blue">|</Text>
            </>)}
            {(data?.principals || []).length > 10 && <Text ml="xs" color="blue" style={{cursor: 'pointer'}} onClick={() => setShowList(data?.principals)}>Ver todas...</Text>}
        </Group>

        <Grid justify="center" mt="lg">
            <Grid.Col md={4} sm={12}>
                <Paper p="lg" shadow="md">
                    <Timeline active={(data?.history || []).length} bulletSize={26} lineWidth={2}>
                        {(data?.history || []).map((his, index) =>
                            <Timeline.Item bullet={<Text style={{color: 'white'}} size="xs">{index+1}</Text>} title={his.title}>
                                <Text color="dimmed" size="sm">{his.description || his.title}</Text>
                                {his.link && <div style={{textAlign: 'right'}}>
                                        <Text 
                                            onClick={() => {window.open(his.link, "_blank")}}
                                            style={{cursor: 'pointer', fontSize: 13, textAlign: 'right'}}
                                            variant="link"
                                            component="span"
                                            inherit>Mais detalhes</Text>
                                </div>}
                                <Tooltip position="bottom" label={moment(his.created_at).format("DD/MM/YYYY HH:mm")}><Text size="xs" mt={4}>{spentTime(his.created_at)}</Text></Tooltip>
                            </Timeline.Item>
                        ).reverse()}
                    </Timeline>
                </Paper>
            </Grid.Col>
            <Grid.Col md={5} sm={12}>
                <Paper shadow="md" mb="md" p="lg">
                    {data?.description}
                </Paper>
                <Paper shadow="md" mb="md" p="lg">
                    <Title order={3} mb="md">Documentação</Title>
                    <Grid>
                        {(data?.selfFiles || []).map(file => <Grid.Col sm={12} md={4}>
                            <Menu>
                                <Menu.Target>
                                    <div style={{border: '1px solid #DFDFDF', cursor: 'pointer', }}>
                                        <div
                                            style={{                                                
                                                height: 100, 
                                                width: '100%', 
                                                backgroundImage: `url(${file.preview})`, 
                                                backgroundColor: '#DADADA',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                display: 'flex',
                                                position: 'relative'
                                            }}
                                        >
                                            {!file.preview && <FaFile color="white" size={60} />}
                                            <div style={{position: 'absolute', right: 5, bottom: 5, zIndex: 100}}>
                                                <Badge size="xs" color="blue">{file.fileType?.title}</Badge>
                                            </div>
                                        </div>
                                        <div style={{textAlign: 'center', padding: 10}}><Text size="sm">{file.title}</Text></div>
                                    </div>
                                </Menu.Target>

                                <Menu.Dropdown>
                                    <Menu.Item icon={<FaRegArrowAltCircleUp />} onClick={() => {window.open(`/f/${file._id}`, "_blank")}}>Abrir arquivo</Menu.Item>
                                    <Menu.Item icon={<FaCopy />} onClick={() => {
                                        clipboard.copy(`${window.location.host}/f/${file._id}`);
                                        showNotification({
                                            message: "Texto copiado!",
                                            autoClose: 2000
                                        })
                                    }}>Copiar link</Menu.Item>
                                    <Divider />
                                    <Menu.Item color="red" icon={<FaTrash />} onClick={() => {setDeletingFile(file._id)}}>Deletar arquivo</Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        </Grid.Col>)}
                        {role && <Grid.Col sm={12} md={12}>
                            <Popover
                                opened={!!addFile}
                                onClose={() => setAddFile(null)}
                                >
                                <Popover.Target>
                                    <Button onClick={() => setAddFile({})}><FaPlus /></Button>
                                </Popover.Target>
                                <Popover.Dropdown>
                                    <div style={{paddingBottom: 20}}>
                                        {addFile?.file_type_id === 'other'
                                        ? <div ref={otherFileFocus}>
                                            <InputField
                                                type={InputFieldTypes.STRING}
                                                title="Tipo do arquivo"
                                                name="file_type_other"
                                                placeholder="Digite o tipo de arquivo"
                                                style={{flex: 1, marginRight: 10}}
                                                value={addFile?.file_type_other}
                                                onBlur={() => {
                                                    if(!addFile?.file_type_other){
                                                        setAddFile(af => ({...af, file_type_id: undefined}))
                                                    }
                                                }}
                                                onChange={data => setAddFile(af => ({...af, ...data}))}
                                            />
                                        </div>
                                        : <InputField
                                            type={InputFieldTypes.SELECT}
                                            title="Tipo do arquivo"
                                            options={[
                                                ...fileTypes.map(ft => ({value: `${ft._id}`, label: ft.title})),
                                                {value: "other", label: "Outro"}
                                            ]}
                                            value={addFile?.file_type_id}
                                            name="file_type_id"
                                            onChange={data => setAddFile(af => ({...af, ...data}))}
                                        />}
                                    </div>

                                    <InputField
                                        mb="md"
                                        title="Título"
                                        type={InputFieldTypes.STRING}
                                        name="title"
                                        value={addFile?.title}
                                        onChange={data => setAddFile(af => ({...af, ...data}))}
                                    />
                                    <InputField
                                        type={InputFieldTypes.FILE}
                                        name="file"
                                        value={addFile?.file}
                                        loading={loadingUploadFile}
                                        onChange={({file}) => uploadFile(file)}
                                    />
                                    <Group mt="md">
                                        <Button loading={loadingSaveFile} color="blue" onClick={saveFile}>Salvar</Button>
                                    </Group>
                                </Popover.Dropdown>
                            </Popover>
                        </Grid.Col>}
                    </Grid>
                </Paper>
                <Paper shadow="md" mb="md" p="lg">
                    <Title order={3} mb="md">Insights</Title>

                    <Grid>
                        <Grid.Col sm={12} md={6}>
                            <UnstyledButton onClick={() => setShowInsights("commissions")}>
                                <Group>
                                    <Avatar size={40} color="blue"><FaSearchLocation /></Avatar>
                                    <div style={{flex: 1}}>
                                        <Text>Possíveis Comissões</Text>
                                        <Text size="xs" color="dimmed">Clique para calcular</Text>
                                    </div>
                                </Group>
                            </UnstyledButton>
                        </Grid.Col>
                        {/* <Grid.Col sm={12} md={6}>
                            <UnstyledButton onClick={() => setShowInsights("similars")}>
                                <Group>
                                    <Avatar size={40} color="blue"><FaSearchLocation /></Avatar>
                                    <div style={{flex: 1}}>
                                        <Text>Pautas similares</Text>
                                        <Text size="xs" color="dimmed">Clique para calcular</Text>
                                    </div>
                                </Group>
                            </UnstyledButton>
                        </Grid.Col> */}
                    </Grid>
                </Paper>
                {(data?.authors || []).length > 0 && <Paper shadow="md" mb="md" p="lg">
                    <Title order={3}>{(data?.authors || []).length > 1 ? 'Autores' : 'Autor'}</Title>
                    <div style={{padding: '20px 0'}}>
                        {(data?.authors || []).map(a => a.name).join(", ")}
                    </div>
                </Paper>}
                <Paper shadow="md" mb="md" p="lg">
                    <Title order={3}>Votações</Title>
                    {(data?.votes || []).length === 0 && <Text pt="lg" size="sm">Nenhuma votação</Text>}
                    {(data?.votes || []).map(v => <div style={{padding: "10px 0"}}>
                        <Title order={6}>
                            {moment(v.created_at).format("DD/MM/YYYY HH:mm")} - {v.title}
                            <br/>
                            Resultado: {v.result ? v.result : "Pendente"}
                        </Title>
                        <div style={{padding: '20px 0', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', overflowX: 'auto'}}>
                            {(v.items || []).map(item => <Paper p="md" mr="md" style={{minWidth: 250, display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
                                <Avatar size="lg" src={item.image} />
                                <div style={{flex: 1, padding: '0 0 0 20px'}}>
                                    <Text>{item.name}</Text>
                                    <Text size='xs'>{item.company}</Text>
                                    <div style={{textAlign: 'right'}}>
                                        <Badge color="gray">{item.vote}</Badge>
                                    </div>
                                </div>
                            </Paper>)}
                        </div>
                    </div>)}
                </Paper>
            </Grid.Col>
            <Grid.Col md={3} sm={12}>
                <Paper p="lg" shadow="md" mb="md">
                    <InputField
                        type={InputFieldTypes.TEXTAREA}
                        name="comment"
                        mb="sm"
                        value={newComment.comment}
                        title="Comentário"
                        onChange={d => setNewComment(nc => ({...nc, ...d}))}
                    />
                    <InputField
                        type={InputFieldTypes.SELECT}
                        name="destin"
                        title="Privacidade"
                        mb="sm"
                        value={newComment.destin}
                        onChange={d => setNewComment(nc => ({...nc, ...d}))}
                        options={[
                            { label: "Público", value: "public" },
                            { label: "Privado", value: "private" },
                        ]}
                    />
                    <Group mb="sm" grow><Button loading={loadingComment} onClick={saveComment} leftIcon={<FaPaperPlane />}>Enviar</Button></Group>
                </Paper>
                <Paper p="lg" shadow="md">
                    {(comments || []).map(item => <div>
                        <div style={{marginBottom: 10}}>
                            <Group mb="sm"> 
                                <Avatar size={40} color="blue" src={item.author.image} />
                                <Text  color="blue" style={{flex: 1}}>{item.author.name}</Text>
                            </Group>
                            <Text size="sm">{item.comment}</Text>
                            <Text size="xs" color="dimmed" align='right'>{moment(item.datetime).format("DD/MM/YYYY HH:mm")}</Text>
                        </div>
                    </div>)}
                </Paper>
            </Grid.Col>
        </Grid>

        <Dialog
            opened={!!deletingFile}
            withCloseButton
            onClose={() => setDeletingFile(null)}
            size="lg"
            radius="md"
        >
            <Text size="sm" style={{ marginBottom: 10 }} weight={500}>
                Realmente deseja deletar esse arquivo?
            </Text>

            <Group align="flex-end">
                <Button color="red" onClick={confirmDelete}>Confirmar</Button>
            </Group>
        </Dialog>
        
        <Modal
            opened={!!showList}
            withCloseButton
            onClose={() => setShowList(null)}
            size="xs"
            radius="md"
        >
            <div style={{overflowY: 'auto', maxHeight: '60vh'}}>
                {(showList || []).map(item => <UnstyledButton onClick={() => { setShowList(null); openTheme(item) }}>
                    <Group mb="md">
                        <Avatar size={40} color="blue"><FaFile /></Avatar>
                        <div>
                            <Text>{item.title}</Text>
                            <Text size="xs" color="dimmed">Clique visualizar</Text>
                        </div>
                    </Group>
                </UnstyledButton>)}
            </div>
        </Modal>

        <Modal
            opened={!!showInsights}
            withCloseButton
            onClose={() => setShowInsights(null)}
            size="lg"
            radius="xs"
        >          
            {showInsights && <ThemeInsights id={id} type={showInsights} />}
        </Modal>
    </div>
}